import React, { useCallback } from 'react';
import { isEuEnv } from '@mb/lib';
import {
  getAddToCardLabels,
  isRestricted,
} from '@productSearch/helpers/common';
import { openRestrictedItemModal } from '@restrictedItemsModal';
import { ProductCard } from 'components/Cards/ProductCard';
import {
  ColorVariant,
  ProductCardActionType,
  TopInfoItem,
} from 'components/Cards/ProductCard/ProductCard.types';

import palette from 'theme/palette';
import {
  getMaterialUrl,
  getSubCategoryToShow,
  isGenslerUser,
  isSearch,
} from './resultCard.utils';
import { FinishGroupItem, ResultCardProps } from './types';
import { useResultCard } from './useResultCard';
import { sendAnalytics } from '../../../api/events';
import { isUnavailable } from '../../../utils/helpers';
import { individualToggle } from '../../Filter/FamilyViewSwitch';

const ResultCard = React.memo<ResultCardProps>((props) => {
  const {
    material: product,
    imageSize = 252,
    selectedProject,
    onProductClick,
    onCreateProjectClick,
    onProjectClick,
    projects,
    isMultiselect,
    onMaterialSelect,
    isSelected,
    searchToggle,
    cartItemsIds = [],
    onAddToFavorites,
    isColorway = false,
    isMobile,
    isSmallDesktop,
    isHoverAllowed,
    isOutlined,
    isSearchPage,
    onContextMenuClick,
    onAddToCart,
    itemListId,
    itemListName,
    analyticsEventLocation,
    index,
    userLang = 'en',
    selectedVariants,
    multiselectIds,
    shouldShowVariants,
    onColorVariantsClick,
    isSelectboxDisabled,
    isCompareLimitReached,
    showOnHoverOnlyOvernight,
    isCompactDescription,
    shouldShowHoverSelect = true,
    shouldHideFavoritesButton = false,
    customTopRightButtonLabel,
    customTopRightButtonCallback,
    shouldHideActionButton = false,
    cardStaticLabels,
    loadingCartItemIds,
  } = props;

  const {
    name,
    residentialCommercial,
    priceSign,
    entityId,
    manufacturer,
    manufacturerSku,
    blurhash,
    isInRealTimeStock,
    isNew,
    isJustAdded,
    colorwayId,
    childrenCount,
  } = product;

  const {
    isVariantInStock,
    isPaint,
    analyticsProps,
    imageUrl,
    variantEntityId,
    variantImageUrl,
    memoizedVariants,
    checkVariantStock,
  } = useResultCard({
    product,
    imageSize,
    selectedVariants,
  });

  const isProduct = product?.isProduct;
  const urlPrefix = isEuEnv ? `/${userLang}` : '';
  const cardLabel = isColorway ? product?.title : name;
  const { cardLabels, internalLabels } = cardStaticLabels;

  const conditionalLabels = {
    ...getAddToCardLabels(
      {
        isGeoRestricted: product.isGeoRestricted,
        isProjectRestricted: product.isProjectRestricted,
        isUnavailable: isUnavailable(product, variantEntityId),
      },
      cardLabels,
    ),
    saveButtonLabel: customTopRightButtonLabel || cardLabels?.saveButtonLabel,
    checkboxTooltipTitle:
      isCompareLimitReached && isSelectboxDisabled
        ? cardLabels?.checkboxTooltipTitle
        : undefined,
  };
  const finalLabels = { ...(cardLabels || {}), ...conditionalLabels };

  const isInCart = cartItemsIds.includes(variantEntityId || entityId);
  const itemId = variantEntityId || entityId;
  const isParentsShown = searchToggle !== individualToggle;

  const materialUrl = getMaterialUrl(
    product,
    itemId,
    urlPrefix,
    itemListId,
    itemListName,
    index,
    searchToggle,
  );

  const handleFavoritesClick = () => {
    onAddToFavorites?.(
      {
        ...product,
        entityId: itemId || product.entityId,
      },
      index || 0,
    );
  };

  const handleItemSelect = () => {
    if (!isSelectboxDisabled) {
      onMaterialSelect?.({
        ...product,
        entityId: itemId,
      });
    }
  };

  const handleVariantClick = (item: ColorVariant) => {
    // Check stock for the selected variant
    if (item?.entity_id != null) {
      checkVariantStock(item.entity_id);
    }

    onColorVariantsClick?.({
      ...product,
      exactColor: item?.exact_color,
      entityId: item?.entity_id,
      thumbnailImageUrl: item?.image_url,
      parentEntityId: entityId,
    });
  };

  const handleAddToBoxClick = () => {
    onAddToCart?.({
      product: {
        ...product,
        entityId: itemId,
        imageUrl: variantImageUrl || product.thumbnailImageUrl,
      },
      productEventData: {
        subtitle1,
        subtitle2: cardLabel,
      },
    });
  };

  const handleProductClick = async (id?: string) => {
    if (
      !isProduct &&
      isRestricted({
        ...product,
        entityId: itemId,
      }) &&
      !isMobile
    ) {
      await sendAnalytics(
        { id: [String(itemId)], type: 'view', ...analyticsProps },
        1200,
      );

      openRestrictedItemModal({
        product: {
          ...product,
          entityId: itemId,
        },
        subtitle1,
        subtitle2: cardLabel,
        projectId: selectedProject?.id,
      });
      return;
    }

    if (isMultiselect) {
      if (!isSelectboxDisabled) {
        onMaterialSelect?.(product);
      }
    } else {
      const newUrl =
        id != null && colorwayId == null
          ? `${urlPrefix}${product.url}?activeChild=${id}`
          : materialUrl;
      onProductClick?.({
        ...product,
        url: newUrl,
        manufacturerSku,
        thumbnailImageUrl: variantImageUrl || imageUrl,
        entityId: variantEntityId || id || colorwayId || itemId,
        isColorApplied: Boolean(variantEntityId),
        index,
      });
    }
  };

  const onCreateProject = useCallback(() => {
    onCreateProjectClick?.({ analyticsEventLocation });
  }, [analyticsEventLocation, onCreateProjectClick]);

  const handleActionButtonClick = async (id?: string) => {
    try {
      await handleProductClick(id);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRightClick = () => {
    onContextMenuClick?.({
      ...product,
      entityId: variantEntityId || itemId,
    });
  };

  const handleProjectClick = useCallback(
    (id: number) => {
      onProjectClick?.(id, { analyticsEventLocation });
    },
    [analyticsEventLocation, onProjectClick],
  );

  const usageList =
    residentialCommercial?.length > 0 ? residentialCommercial : null;

  const priceLabel = isPaint ? null : priceSign;

  const subcategoryToShow = getSubCategoryToShow(product);

  const usageType =
    usageList?.map((usage: string) => usage.split(' ')[0]) || [];
  const colorwaysNumber =
    product.colorVariants?.total_count > 1
      ? `${product.colorVariants?.total_count} ${internalLabels?.colorwaysLabel}`
      : null;
  const features = [
    priceLabel,
    subcategoryToShow,
    ...usageType,
    colorwaysNumber,
  ].filter((item) => Boolean(item));
  const count =
    product.colorVariants?.total_count ||
    (Number(childrenCount) > 1 ? childrenCount : undefined);

  // handle Top Card Labels
  const shouldShowGpsLabel = product.isGpsCompliant && isGenslerUser;
  const topLabels: TopInfoItem[] = shouldShowGpsLabel
    ? [
        {
          label: 'GPS',
          iconName: 'check',
          bgColor: palette.secondary?.main,
          textColor: '#e82011', // Gensler brand color
        },
      ]
    : [];
  if (isNew) {
    topLabels.push({ label: internalLabels?.newLabel });
  } else if (isJustAdded) {
    topLabels.push({ label: internalLabels?.justAddedLabel });
  }
  const blurhashString = blurhash?.length >= 6 ? blurhash : null;
  const shouldShowSelectOnHover =
    !isMobile && isSearchPage && shouldShowHoverSelect;
  const finalCardUrl = isProduct
    ? materialUrl?.split('?activeChild')?.[0]
    : materialUrl;
  const actionType: ProductCardActionType = isProduct
    ? 'customAction'
    : 'addToCart';
  const showProjectsDropdown = !isMobile && !isProduct;
  const finishesCount =
    product.components?.reduce(
      (totalCount: number, finishGroup: { items: FinishGroupItem[] }) =>
        totalCount + (finishGroup.items?.length ?? 0),
      0,
    ) ?? 0;
  const finishesCountLabel = `${finishesCount} ${internalLabels?.finishes}`;
  const taxonomyLabel = product.taxonomy?.[0]?.split('>')?.pop();
  const subtitle1 =
    isProduct && finishesCount > 0
      ? finishesCountLabel
      : subcategoryToShow || taxonomyLabel;
  const showColorVariants =
    shouldShowSelectOnHover && isSearch && isParentsShown && shouldShowVariants;

  const isAddToCartLoading = loadingCartItemIds?.includes(String(itemId));
  const actionButtonText = internalLabels?.sampleFinishesLabel;

  const isMultiselected = isSelected || (multiselectIds ?? []).includes(itemId);
  return (
    <ProductCard
      showAdvancedInfo
      showHoverFeatures
      showExtraInfo={false}
      isSmallDesktop={isSmallDesktop}
      hideActionButton={shouldHideActionButton}
      shouldHideFavoritesButton={shouldHideFavoritesButton}
      showOnHoverOnlyOvernight={showOnHoverOnlyOvernight}
      isCompactDescription={isCompactDescription}
      isSelectboxDisabled={isSelectboxDisabled}
      withColorVariants={showColorVariants}
      title={manufacturer}
      label={cardLabel}
      subtitle1={subtitle1}
      subtitle2={cardLabel}
      entityId={variantEntityId || entityId}
      imageUrl={variantImageUrl || imageUrl}
      projects={projects}
      productUrl={finalCardUrl}
      features={features}
      extraInfo={[]}
      topInfoItems={topLabels}
      // Important: ?? is needed here to avoid showing parents stock
      isOvernight={isVariantInStock ?? isInRealTimeStock}
      isMobileDevice={isMobile}
      showSelectOnHover={shouldShowSelectOnHover}
      showSelect={isMultiselect}
      isHoverAllowed={isHoverAllowed}
      isOutlined={isOutlined}
      isSelected={isMultiselected}
      showBag
      childrenNumber={count}
      showProjectsDropdown={showProjectsDropdown}
      blurhash={blurhashString}
      actionType={actionType}
      actionButtonText={actionButtonText}
      onClickAddToFavorites={isMobile ? undefined : handleFavoritesClick}
      selectedProject={selectedProject}
      onAddToBag={handleAddToBoxClick}
      isAddToCartLoading={isAddToCartLoading}
      isAddedToCart={isInCart}
      onProductClick={handleProductClick}
      onActionButtonClick={handleActionButtonClick}
      onProjectClick={handleProjectClick}
      onSelected={handleItemSelect}
      onCreateProject={onCreateProject}
      onRightClick={handleRightClick}
      colorVariants={memoizedVariants}
      colorVariantsNumber={product.colorVariants?.total_count}
      onVariantClick={handleVariantClick}
      activeVariantId={variantEntityId}
      customTopRightButtonCallback={customTopRightButtonCallback}
      imageDecodeMode="lazy"
      {...finalLabels}
    />
  );
});

ResultCard.displayName = 'ResultCard';

export { ResultCard };
